import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Checkbox,
  TextField,
  FormControlLabel,
  Button,
  Box,
  Grid,
  Typography,
  Container,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import "../../theme-vars.css"; // <-- Import theme variables
import { connect, useDispatch } from "react-redux";
import { createDaysTimeSlots, fetchDaysTimeSlots } from "../../Services/organizations";
import Loading from "../../Components/Loading/Loading";
import { showSnackbarAction } from "../../Redux/actions";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";

type Props = {
  history: any;
  organizationId: any;
  apiHeaders: any;
  theme: any;
};

type ScheduleItem = {
  day: string;
  closed: boolean;
  opensAt: string;
  closesAt: string;
};

const DaysConfiguration: FunctionComponent<Props> = ({
  organizationId,
}) => {
  const dispatcher = useDispatch();
  const [load, setLoad] = useState(false);

  const [schedule, setSchedule] = useState<ScheduleItem[]>([
    { day: "Monday", closed: false, opensAt: "09:00", closesAt: "19:00" },
    { day: "Tuesday", closed: false, opensAt: "09:00", closesAt: "19:00" },
    { day: "Wednesday", closed: false, opensAt: "09:00", closesAt: "19:00" },
    { day: "Thursday", closed: false, opensAt: "09:00", closesAt: "19:00" },
    { day: "Friday", closed: false, opensAt: "09:00", closesAt: "19:00" },
    { day: "Saturday", closed: true, opensAt: "", closesAt: "" },
    { day: "Sunday", closed: true, opensAt: "", closesAt: "" },
  ]);

  // ✅ Responsive handling
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  useEffect(() => {
    if (organizationId) {
      const unsubscribe = fetchDaysTimeSlots(organizationId, (data) => {
        if (data.length > 0) {
          setSchedule(data);
        } else {
          console.log("No valid daysTimeSlots found, retaining default schedule.");
        }
      });
      return () => unsubscribe();
    }
  }, [organizationId]);

  const handleInputChange = (index: number, field: string, value: string) => {
    const updatedSchedule = [...schedule];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    setSchedule(updatedSchedule);
  };

  const handleCheckboxChange = (index: number) => {
    const updatedSchedule = [...schedule];
    updatedSchedule[index].closed = !updatedSchedule[index].closed;
    if (updatedSchedule[index].closed) {
      updatedSchedule[index].opensAt = "";
      updatedSchedule[index].closesAt = "";
    } else {
      updatedSchedule[index].opensAt = "09:00";
      updatedSchedule[index].closesAt = "19:00";
    }
    setSchedule(updatedSchedule);
  };

  const handleSave = () => {
    for (const item of schedule) {
      if (!item.closed && (!item.opensAt || !item.closesAt)) {
        dispatcher(
          showSnackbarAction(
            `Please provide valid opening and closing times for ${item.day}.`,
            "error"
          )
        );
        return;
      }
    }
    setLoad(true);
    createDaysTimeSlots(organizationId, schedule, dispatcher, (val) => setLoad(val));
  };

  return (
    <>
      {load && <Loading />}
      <Container
        maxWidth="md"
        sx={{
          bgcolor: "background.paper",
          boxShadow: isMobile ? undefined : 3,
          borderRadius: isMobile ? undefined : 2,
          textAlign: "center",
          border: "1px solid #e0e0e0",
          width: isMobile ? "100vw" : "100%",
          minWidth: isMobile ? undefined : "900px",
          marginTop: isMobile ? "6%" : "5%",
          marginBottom: isMobile ? "80px" : "20px",
          padding: isMobile ? "16px" : "32px",
          marginLeft: isMobile ? "0px" : "",
          overflowX: "auto",
          fontFamily: "var(--font-family-main)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5, fontSize: "var(--font-size-large)" }}>
          Weekly Schedule Configuration
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ mb: 1, color: "text.secondary", fontSize: "var(--font-size-medium)" }}
        >
          Customize your organization's weekly schedule. You can set operating
          hours or mark days as closed.
        </Typography>

        {/* Days List */}
        {schedule?.map((item, index) => (
          <Paper
            key={index}
            elevation={1}
            sx={{
              borderRadius: 2,
              p: 2,
              mb: 2,
              border: "1px solid #f0f0f0",
            }}
          >
            {/* Header: Day + Checkbox */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: item.closed ? 0 : 0.3,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  // fontWeight: "bold",
                  fontSize: "var(--font-size-medium)",
                  fontFamily: "var(--font-family-main)",
                  color: item.closed ? "red" : "green",
                }}
              >
                {item.day}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item.closed}
                    onChange={() => handleCheckboxChange(index)}
                    sx={{
                      color: item.closed ? "red" : "green",
                      "& .MuiSvgIcon-root": {
                        fontSize: "var(--font-size-medium)",
                        fontFamily: "var(--font-family-main)"
                      },
                    }}
                  />
                }
                label={<span style={{ fontSize: "var(--font-size-medium)", fontFamily: "var(--font-family-main)" }}>{item.closed ? "Closed" : "Open"}</span>}
              />
            </Box>
            {/* Time Fields */}
            {!item.closed && (
              <Grid container spacing={2} style={{}}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="time"
                    label="Opens At"
                    value={item.opensAt}
                    onChange={(e) =>
                      handleInputChange(index, "opensAt", e.target.value)
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      style: {
                        height: "8px", fontSize: "var(--font-size-medium)",
                        fontFamily: "var(--font-family-main)"
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="time"
                    label="Closes At"
                    value={item.closesAt}
                    onChange={(e) =>
                      handleInputChange(index, "closesAt", e.target.value)
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      style: {
                        height: "8px", fontSize: "var(--font-size-medium)",
                        fontFamily: "var(--font-family-main)"
                      }
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
        ))}
        {!isMobile && (
          <Box
            sx={{
              position: { xs: "fixed", sm: "static" },
              bgcolor: "white",
              borderTop: { xs: "1px solid #ddd", sm: "none" },
              boxShadow: { xs: "0 -2px 6px rgba(0,0,0,0.1)", sm: "none" },
            }}
          >
            <Container maxWidth="md">
              <StyledSubmitButton
                onClick={handleSave}
                themeMode={themeMUI.palette.mode}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} />
                  </span>
                ) : (
                  <>Submit</>
                )}
              </StyledSubmitButton>
            </Container>
          </Box>
        )}
      </Container>
      {/* Sticky Save Button on Mobile */}
      {isMobile && (
        <Box
          sx={{
            position: { xs: "fixed", sm: "static" },
            bottom: { xs: 0, sm: "auto" },
            left: 0,
            right: 0,
            bgcolor: "white",
            p: 2,
            borderTop: { xs: "1px solid #ddd", sm: "none" },
            boxShadow: { xs: "0 -2px 6px rgba(0,0,0,0.1)", sm: "none" },
          }}
        >
          <Container maxWidth="md">
            <StyledSubmitButton
              onClick={handleSave}
              themeMode={themeMUI.palette.mode}
              isMobile={isMobile}
            >
              {load ? (
                <span style={{ display: "flex", justifyContent: "center" }}>
                  <Circles color="#fff" height={20} width={20} />
                </span>
              ) : (
                <>Submit</>
              )}
            </StyledSubmitButton>
          </Container>
        </Box>
      )}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  organizationId: state.organization.id,
  apiHeaders: state.apiHeaderList.data,
  theme: state.theme.isLightMode,
});

export default connect(mapStateToProps)(DaysConfiguration);
